import React, { FC, JSX, useState } from 'react';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async'; // For SEO

// --- Interface & Data for FAQ ---
interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "Are your products 100% natural?",
    answer: "Yes, all AyushVatika products are crafted using 100% natural and herbal ingredients, free from harmful chemicals and parabens."
  },
  {
    question: "What is your return policy?",
    answer: "We offer a hassle-free 7-day return policy for damaged or incorrect items. Please contact our support team within 48 hours of delivery."
  },
  {
    question: "Do you ship internationally?",
    answer: "Currently, we ship across India. We are working on enabling international shipping soon."
  },
  {
    question: "How can I track my order?",
    answer: "Once your order is shipped, you will receive a tracking link via email and SMS to monitor your package in real-time."
  }
];

// --- Detail Card Component ---
interface DetailCardProps {
  icon: JSX.Element;
  label: string;
  value: string;
  href?: string;
}

const DetailCard: FC<DetailCardProps> = ({ icon, label, value, href }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    viewport={{ once: true }}
    className="flex flex-col items-center justify-center p-6 bg-green-50 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-green-100 text-center h-full"
  >
    <div className="text-emerald-600 text-3xl mb-3">{icon}</div>
    <h4 className="font-bold text-gray-800 mb-1">{label}</h4>
    {href ? (
      <a href={href} className="text-gray-600 hover:text-emerald-700 transition-colors font-medium break-all">
        {value}
      </a>
    ) : (
      <p className="text-gray-600 font-medium break-words w-full">{value}</p>
    )}
  </motion.div>
);

// --- FAQ Item Component ---
const FAQAccordion: FC<{ item: FAQItem }> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center py-4 text-left focus:outline-none group"
        aria-expanded={isOpen}
      >
        <span className={`text-lg font-medium transition-colors ${isOpen ? 'text-emerald-600' : 'text-gray-700 group-hover:text-emerald-600'}`}>
          {item.question}
        </span>
        <span className="text-gray-400 ml-4">
          {isOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="text-gray-600 pb-4 leading-relaxed">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Main Page Component ---
const ContactPage = () => {
  // Structured Data for SEO (Organization)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AyushVatika",
    "url": "https://www.ayushvatika.com",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-9412220200",
      "contactType": "Customer Service"
    }
  };

  return (
    <>
      {/* --- SEO Head Section --- */}
      <Helmet>
        <title>Contact Us - AyushVatika | Herbal & Natural Products Support</title>
        <meta name="description" content="Get in touch with AyushVatika. Have queries about our herbal products? Call us, email us, or visit our store in New Delhi." />
        <meta name="keywords" content="AyushVatika contact, ayurveda support, herbal products help, customer care" />
        <link rel="canonical" href="https://www.ayushvatika.com/contact" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
        
        {/* --- Main Contact Section --- */}
        <div className="max-w-6xl w-full bg-white shadow-xl rounded-2xl overflow-hidden mb-12">
          <div className="p-8 md:p-12">
            <header className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Get in Touch</h1>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                We'd love to hear from you! Whether you have a question about our products, pricing, or anything else, our team is ready to answer all your questions.
              </p>
            </header>

            <div className="grid gap-12 lg:grid-cols-2">
              {/* Left Column: Contact Form */}
              <section aria-label="Contact Form">
                <form className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      placeholder="Enter your name"
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      placeholder="you@example.com"
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-gray-700 text-sm font-bold mb-2">Your Message</label>
                    <textarea
                      id="message"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all resize-none"
                      rows={5}
                      placeholder="How can we help you today?"
                      aria-required="true"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-emerald-600 text-white font-bold py-3.5 px-4 rounded-lg hover:bg-emerald-700 active:transform active:scale-[0.98] transition-all shadow-lg shadow-emerald-200"
                  >
                    Send Message
                  </button>
                </form>
              </section>

              {/* Right Column: Contact Details */}
              <section aria-label="Contact Information" className="flex flex-col justify-between space-y-6">
                <DetailCard
                  icon={<FaMapMarkerAlt />}
                  label="Our Office"
                  value="Gautham Bhudhh Nager B-127, Block B near by Sector-15 Metro Station Noida, India - 201301"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <DetailCard
                    icon={<FaPhoneAlt />}
                    label="Phone"
                    value="+91 9412220200"
                    href="tel:+919412220200"
                  />
                  <DetailCard
                    icon={<FaEnvelope />}
                    label="Email"
                    value="info@ayushvatika.com"
                    href="mailto:info@ayushvatika.com"
                  />
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* --- FAQ Section --- */}
        <section className="max-w-5xl w-full" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-bold text-center text-emerald-800 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-2xl shadow-md p-6 md:p-8 border border-gray-100">
            {faqs.map((faq, index) => (
              <FAQAccordion key={index} item={faq} />
            ))}
          </div>
        </section>

      </div>
    </>
  );
};

export default ContactPage;